import React, { useState, useRef, useEffect } from 'react';
import { uploadVoiceSample, cloneVoiceFromUrl } from '../services/aiService';
import { LoadingSpinner } from './icons/LoadingSpinner';
import { CloseIcon } from './icons/CloseIcon';
import { UploadIcon } from './icons/UploadIcon';

/**
 * VoiceClonerModal — records or uploads an audio sample and clones it via
 * ElevenLabs. Costs 200 credits.
 *
 * Flow:
 *   record / upload  ─▶  POST /api/ai/upload-voice-sample   → { url }
 *                    ─▶  POST /api/ai/clone-voice           → { voice_id }
 *                    ─▶  onCloned({ voiceId, voiceName, sourceUrl })
 *
 * Props
 *   voiceNameSuggested — string; pre-fills the voice-name input
 *   onCloned(voice)    — called after successful clone
 *   onClose()          — dismiss the modal
 *   onTrackCreditsUsed — optional, called with credits_used integer
 */
export const VoiceClonerModal = ({ voiceNameSuggested = '', onCloned, onClose, onTrackCreditsUsed }) => {
    const [voiceName, setVoiceName] = useState(voiceNameSuggested || `Ad Creator Voice ${new Date().toLocaleDateString()}`);
    const [audioBlob, setAudioBlob] = useState(null); // { blob, url, name }
    const [recording, setRecording] = useState(false);
    const [cloning, setCloning] = useState(false);
    const [error, setError] = useState(null);
    const mediaRecorderRef = useRef(null);
    const streamRef = useRef(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        return () => {
            // Stop mic if the modal closes mid-recording
            if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
            if (audioBlob?.url) URL.revokeObjectURL(audioBlob.url);
        };
    }, [audioBlob?.url]);

    const startRecording = async () => {
        setError(null);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
            const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
            const chunks = [];
            rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
            rec.onstop = () => {
                const type = rec.mimeType || 'audio/webm';
                const blob = new Blob(chunks, { type });
                const url = URL.createObjectURL(blob);
                setAudioBlob({ blob, url, name: `recording.${(type.split('/')[1] || 'webm').split(';')[0]}` });
                stream.getTracks().forEach(t => t.stop());
                streamRef.current = null;
            };
            mediaRecorderRef.current = rec;
            rec.start();
            setRecording(true);
        } catch (e) {
            setError(`Microphone access denied or unavailable: ${e.message}`);
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
        }
        setRecording(false);
    };

    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('audio/')) {
            setError('Please choose an audio file (mp3, wav, ogg, webm, m4a).');
            return;
        }
        const MAX_MB = 25;
        if (file.size > MAX_MB * 1024 * 1024) {
            setError(`Sample too large (>${MAX_MB} MB).`);
            return;
        }
        const url = URL.createObjectURL(file);
        setAudioBlob({ blob: file, url, name: file.name });
        setError(null);
    };

    const handleClone = async () => {
        if (!audioBlob?.blob) { setError('Record or upload a sample first.'); return; }
        if (!voiceName.trim()) { setError('Give this voice a name.'); return; }
        setCloning(true);
        setError(null);
        try {
            const { url } = await uploadVoiceSample(audioBlob.blob, audioBlob.name);
            const { voiceId, creditsUsed } = await cloneVoiceFromUrl(url, voiceName.trim());
            if (typeof onTrackCreditsUsed === 'function' && creditsUsed) onTrackCreditsUsed(creditsUsed);
            onCloned({ voiceId, voiceName: voiceName.trim(), sourceUrl: url });
            onClose();
        } catch (e) {
            setError(e.message || 'Voice cloning failed.');
        } finally {
            setCloning(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4" onClick={onClose} data-testid="voice-cloner-modal">
            <div className="bg-[#111119] border border-gray-800 rounded-2xl w-full max-w-lg p-5 space-y-4" onClick={e => e.stopPropagation()}>
                <header className="flex items-center justify-between">
                    <div>
                        <h2 className="text-base font-bold text-white">Clone your voice</h2>
                        <p className="text-[11px] text-zinc-500">Record or upload a 15-60s sample. Costs <span className="text-amber-300 font-semibold">200 credits</span> on success.</p>
                    </div>
                    <button onClick={onClose} className="p-1.5 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white" data-testid="voice-cloner-close">
                        <CloseIcon className="w-5 h-5" />
                    </button>
                </header>

                {error && <div className="p-2.5 bg-red-900/30 border border-red-700/50 rounded-lg text-xs text-red-300" data-testid="voice-cloner-error">{error}</div>}

                <div>
                    <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Voice name</label>
                    <input
                        value={voiceName}
                        onChange={e => setVoiceName(e.target.value)}
                        placeholder="e.g. Warm narrator"
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 placeholder-gray-600 focus:border-purple-500 focus:outline-none"
                        data-testid="voice-cloner-name-input"
                    />
                </div>

                <div className="border border-gray-800 rounded-xl p-4 space-y-3">
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Voice sample</p>

                    {audioBlob?.url ? (
                        <div className="space-y-2">
                            <audio src={audioBlob.url} controls className="w-full" data-testid="voice-cloner-preview" />
                            <button
                                onClick={() => { URL.revokeObjectURL(audioBlob.url); setAudioBlob(null); }}
                                className="text-xs text-zinc-400 hover:text-red-300"
                                data-testid="voice-cloner-clear"
                            >
                                Clear sample
                            </button>
                        </div>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            {!recording ? (
                                <button
                                    onClick={startRecording}
                                    className="px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5"
                                    data-testid="voice-cloner-record"
                                >
                                    <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> Record
                                </button>
                            ) : (
                                <button
                                    onClick={stopRecording}
                                    className="px-3 py-1.5 bg-red-800 hover:bg-red-700 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5"
                                    data-testid="voice-cloner-stop"
                                >
                                    <span className="w-2 h-2 rounded-sm bg-white" /> Stop
                                </button>
                            )}
                            <input type="file" accept="audio/*" ref={fileInputRef} className="hidden" onChange={handleFileUpload} data-testid="voice-cloner-file-input" />
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5"
                                data-testid="voice-cloner-upload"
                            >
                                <UploadIcon className="w-3.5 h-3.5" /> Upload
                            </button>
                            <p className="text-[10px] text-zinc-500 w-full">MP3 / WAV / OGG / WEBM / M4A, up to 25 MB.</p>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-3 pt-1">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white" data-testid="voice-cloner-cancel">Cancel</button>
                    <button
                        onClick={handleClone}
                        disabled={cloning || !audioBlob}
                        className="px-5 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:text-gray-500 text-white text-sm font-semibold rounded-lg flex items-center gap-2"
                        data-testid="voice-cloner-submit"
                    >
                        {cloning ? (<><LoadingSpinner className="w-4 h-4" /> Cloning…</>) : 'Clone voice (200 credits)'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default VoiceClonerModal;
